import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { keys } from "@prismicio/editor-support/Object"
import {
  Document,
  SliceItemContent
} from "@prismicio/types-internal/lib/content"
import type { InfiniteData, QueryClient } from "@tanstack/react-query"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"

const authorOkType = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().optional(),
  uploadedAvatar: z.string().optional()
})

export const languageOkType = z.object({
  id: z.string(),
  name: z.string(),
  is_master: z.boolean().optional()
})

const documentVersionOkType = z
  .discriminatedUnion("status", [
    z.object({ status: z.literal("unclassified") }),
    z.object({ status: z.literal("published") }),
    z.object({ status: z.literal("release"), release_id: z.string() }),
    z.object({ status: z.literal("archived") })
  ])
  .and(
    z.object({
      version_id: z.string(),
      tags: z.array(z.string()),
      custom_type_label: z.string(),
      preview_summary: z.string().optional(),
      preview_image: z.string().optional(),
      last_modified_date: z.coerce.date(),
      author: authorOkType,
      uid: z.string().optional()
    })
  )

export const documentMetaOkType = z.object({
  id: z.string(),
  title: z
    .string()
    .nullish()
    .transform((title) => title ?? "Untitled"),
  custom_type_id: z.string(),
  group_lang_id: z.string(),
  language: languageOkType,
  versions: z.array(documentVersionOkType)
})

const getDocumentListSchema = z.object({
  total: z.number(),
  cursor: z.string().optional(),
  results: z.array(documentMetaOkType)
})

const documentSearchOkType = z
  .object({
    id: z.string(),
    title: z.string(),
    repository_id: z.string(),
    tags: z.array(z.string()).optional()
  })
  .passthrough()

const documentsFullTextSearchAPISchemaOkType = z.object({
  results: z.array(documentSearchOkType)
})

export type Language = z.TypeOf<typeof languageOkType>
export type GetDocumentListSchema = z.TypeOf<typeof getDocumentListSchema>
export type DocumentFullTextSearchApi = z.TypeOf<
  typeof documentsFullTextSearchAPISchemaOkType
>
export type DocumentMeta = z.TypeOf<typeof documentMetaOkType>
export type DocumentVersion = z.TypeOf<typeof documentVersionOkType>
export type DocumentStatus = DocumentVersion["status"]

export function getDocuments(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  queryString: string,
  limit?: number,
  cursor?: string
): Promise<GetDocumentListSchema> {
  const url = new URL("documents", baseUrl)
  if (limit) url.searchParams.set("limit", limit.toString())
  if (cursor) url.searchParams.set("cursor", cursor)
  url.search += `&${queryString}`

  return request(url, {
    credentials: getRequestCredentials(authStrategy),
    okType: getDocumentListSchema
  })
}

export interface GetDocumentListArgs {
  baseUrl: URL
  authStrategy: AuthStrategy
  signal?: AbortSignal | null | undefined

  limit?: number
  cursor?: string
  status?: string[]
  authors?: string[]
  language?: string
  group_lang_id?: string
  document_id?: string[]
  customTypes?: string[]
  tags?: string[]
  term?: string
}

export function getDocumentList(
  args: GetDocumentListArgs
): Promise<GetDocumentListSchema> {
  const {
    baseUrl,
    authStrategy,
    limit,
    cursor,
    status,
    authors,
    language,
    group_lang_id,
    document_id,
    customTypes,
    tags,
    term,
    signal
  } = args

  const url = new URL("documents", baseUrl)

  const params: { name: string; values: string[] }[] = []

  if (limit) params.push({ name: "limit", values: [limit.toString()] })

  if (cursor) params.push({ name: "cursor", values: [cursor] })

  if (status?.length) params.push({ name: "status", values: status })

  if (authors?.length) params.push({ name: "authors", values: authors })

  if (language) params.push({ name: "language", values: [language] })

  if (group_lang_id)
    params.push({ name: "group_lang_id", values: [group_lang_id] })

  if (document_id?.length)
    params.push({ name: "document_id", values: document_id })

  if (customTypes?.length)
    params.push({ name: "customTypes", values: customTypes })

  if (tags?.length) params.push({ name: "tags", values: tags })

  if (term) params.push({ name: "term", values: [term] })

  url.search = new URLSearchParams(createSearchParams(params)).toString()

  return request(url, {
    credentials: getRequestCredentials(authStrategy),
    okType: getDocumentListSchema,
    signal
  })
}

export function searchFullTextDocuments(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  queryString: string,
  options?: Pick<RequestInit, "signal">
): Promise<DocumentFullTextSearchApi> {
  const url = new URL(baseUrl)
  url.search = queryString

  return request(url, {
    credentials: getRequestCredentials(authStrategy),
    okType: documentsFullTextSearchAPISchemaOkType,
    ...options
  })
}

export function makeSearchFullTextDocumentsQueryString(args: {
  repository: string
  searchTerm?: string
  tags?: string[]
  localeId?: string
  customTypesIds?: string[]
}): string {
  const { repository, searchTerm, tags, localeId, customTypesIds } = args
  const searchParams = new URLSearchParams()
  searchParams.append("repository", repository)

  if (searchTerm) {
    searchParams.append("searchTerm", searchTerm)
  }
  if (tags?.length) {
    searchParams.append("tags", tags.toString())
  }
  if (localeId) {
    searchParams.append("localeId", localeId)
  }
  if (customTypesIds?.length) {
    searchParams.append("customTypesIds", customTypesIds.toString())
  }

  return searchParams.toString()
}

type GetDocumentsQueryFilters =
  | "tags"
  | "language"
  | "customTypes"
  | "status"
  | "term"
  | "group_lang_id"
  | "document_id"
  | "uid"
  | "authors"

export type SearchDocumentParams = {
  [S in GetDocumentsQueryFilters]?: string[]
}

export function createSearchParams<T extends string>(
  params: { name: T; values: (string | undefined)[] }[]
): [string, string][] {
  return params.reduce<[string, string][]>((queryParams, currentVariable) => {
    // if current variable has no values, return queryParams as is without adding the current empty variable
    if (
      currentVariable.values.every(
        (value) => value === "" || value === undefined
      )
    )
      return queryParams

    const arr = currentVariable.values.map<[string, string]>((value) => [
      currentVariable.name,
      value ?? ""
    ])
    return queryParams.concat(arr)
  }, [])
}

export function getDocumentByDocumentId(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  id: string
): Promise<DocumentMeta> {
  return request(new URL(`documents/${id}`, baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: documentMetaOkType
  })
}

export const documentSchema = z.custom().transform((value, context) => {
  const document = Document.decode(value)
  if (document._tag === "Left") {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Invalid document.`
    })
    return z.NEVER
  }
  return document.right
})

export function makeGetDocumentsQueryString(
  searchDocumentParams: SearchDocumentParams
) {
  const params = keys(searchDocumentParams).flatMap((name) => {
    const values = searchDocumentParams[name]
    if (!values) return []
    return { name, values }
  })

  const searchDocumentsTerm = new URLSearchParams(
    createSearchParams<keyof SearchDocumentParams>(params)
  ).toString()

  return searchDocumentsTerm
}

// NOTE: We need to enrich the documents from the Search API with the Core API while it doesn't index properties like status and others
export async function getEnrichedSearchDocuments(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  searchDocuments: DocumentFullTextSearchApi["results"]
): Promise<DocumentMeta[]> {
  if (searchDocuments.length === 0) return []

  // Only check 50 documents for now to prevent huge scrolling.
  // Also we need to be a bit cautious with the maximum number of URL characters
  // when GET requesting from CoreAPI.
  const relevantDocumentIds = searchDocuments.slice(0, 50).map(({ id }) => id)

  const enrichedDocuments = await getDocumentList({
    baseUrl,
    authStrategy,
    document_id: relevantDocumentIds
  })

  const idIndexMap = new Map(
    relevantDocumentIds.map((id, index) => [id, index])
  )

  // Sort enrichedDocuments.results based on the order of IDs in relevantDocumentIds
  return enrichedDocuments.results.sort((a, b) => {
    const indexA = idIndexMap.get(a.id)
    const indexB = idIndexMap.get(b.id)
    return (indexA ?? Infinity) - (indexB ?? Infinity)
  })
}

export const sliceItemContentSchema = z.unknown().transform((val, ctx) => {
  const result = SliceItemContent.decode(val)

  // Either.Right = Success
  if (result._tag === "Right") return result.right

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: "Invalid slice content."
  })

  return z.NEVER
})

export const documentKeys = {
  all: () => ["documents"] as const,
  lists: () => [...documentKeys.all(), "list"] as const,
  list: (args: GetDocumentListArgs) => [...documentKeys.lists(), args] as const,
  infiniteLists: () => [...documentKeys.all(), "list:infinite"] as const,
  infiniteList: (args: GetDocumentListArgs) =>
    [...documentKeys.infiniteLists(), args] as const
}

/**
 * Returns updated document or undefined if document should be removed
 */
type UpdateDocumentFunction = (
  document: DocumentMeta,
  page: GetDocumentListSchema
) => DocumentMeta | undefined

export function updateDocumentListData(
  queryClient: QueryClient,
  updateFunction: UpdateDocumentFunction
) {
  queryClient.setQueriesData(
    {
      queryKey: documentKeys.lists()
    },
    (oldData: GetDocumentListSchema | undefined) => {
      if (!oldData) return oldData
      return updateDocumentListPage(oldData, updateFunction)
    }
  )
  queryClient.setQueriesData(
    {
      queryKey: documentKeys.infiniteLists()
    },
    (oldData: InfiniteData<GetDocumentListSchema> | undefined) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        pages: oldData.pages.map((page) =>
          updateDocumentListPage(page, updateFunction)
        )
      }
    }
  )
}

function updateDocumentListPage(
  page: GetDocumentListSchema,
  updateFunction: UpdateDocumentFunction
) {
  const results = page.results.flatMap((document) => {
    const newDocument = updateFunction(document, page)
    return newDocument ? [newDocument] : []
  })

  return {
    ...page,
    results,
    total: page.total + (results.length - page.results.length)
  }
}

export async function invalidateDocumentListData(queryClient: QueryClient) {
  // Infinite lists depend on regular lists, so in order to fetch them correctly we need to do these one after another
  await queryClient.invalidateQueries({
    queryKey: documentKeys.lists()
  })
  await queryClient.invalidateQueries({
    queryKey: documentKeys.infiniteLists()
  })
}
